import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PropertyDetail } from '@pmc/shared';
import { api, ApiFailureError } from '../lib/api';
import { useLanguage } from '../lib/i18n';
import { parseLocalizedNumber, useMathCaptcha } from '../lib/captcha';
import { MathCaptchaField } from './MathCaptchaField';

/**
 * Update the mobile/email on file for a property.
 */
export function UpdateContactDetailsDialog({
  open,
  onClose,
  propertyId,
  currentMobile,
  currentEmail,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  currentMobile: string | null;
  currentEmail: string | null;
}) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const [mobileNo, setMobileNo] = useState(currentMobile ?? '');
  const [emailId, setEmailId] = useState(currentEmail ?? '');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);

  const captchaQuery = useMathCaptcha(open);

  useEffect(() => {
    if (open) {
      setMobileNo(currentMobile ?? '');
      setEmailId(currentEmail ?? '');
      setCaptchaAnswer('');
      setFieldErrors({});
      setBanner(null);
    }
  }, [open, currentMobile, currentEmail]);

  const update = useMutation({
    mutationFn: () =>
      api.put<PropertyDetail>(`/properties/${propertyId}/contact`, {
        mobileNo,
        emailId,
        captchaToken: captchaQuery.data?.token ?? '',
        captchaAnswer: parseLocalizedNumber(captchaAnswer),
      }),
    onSuccess: (property) => {
      queryClient.setQueryData(['property', propertyId], property);
      onClose();
    },
    onError: (err) => {
      setCaptchaAnswer('');
      void captchaQuery.refetch();
      if (err instanceof ApiFailureError) {
        setFieldErrors(err.fields);
        if (Object.keys(err.fields).length === 0) setBanner(err.message);
      } else {
        setBanner((err as Error).message);
      }
    },
  });

  const handleSave = () => {
    const errors: Record<string, string> = {};
    const mob = mobileNo.trim();
    const em = emailId.trim();

    if (mob && !/^[6-9]\d{9}$/.test(mob)) {
      errors.mobileNo = t('invalid_mobile');
    }

    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      errors.emailId = t('invalid_email');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setBanner(null);
    update.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('update_contact_details')}
        <IconButton size="small" onClick={onClose} aria-label={t('cancel')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('update_contact_details_desc')}
        </Typography>

        {banner ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBanner(null)}>
            {banner}
          </Alert>
        ) : null}

        <Stack spacing={2}>
          <TextField
            fullWidth
            type="tel"
            label={t('mobile')}
            value={mobileNo}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 10);
              setMobileNo(val);
              if (fieldErrors.mobileNo) setFieldErrors((prev) => ({ ...prev, mobileNo: '' }));
            }}
            slotProps={{
              htmlInput: {
                maxLength: 10,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              },
            }}
            error={Boolean(fieldErrors.mobileNo)}
            helperText={fieldErrors.mobileNo}
          />
          <TextField
            fullWidth
            type="email"
            label={t('email')}
            value={emailId}
            onChange={(e) => {
              setEmailId(e.target.value);
              if (fieldErrors.emailId) setFieldErrors((prev) => ({ ...prev, emailId: '' }));
            }}
            error={Boolean(fieldErrors.emailId)}
            helperText={fieldErrors.emailId}
          />

          <MathCaptchaField
            challenge={captchaQuery.data}
            isFetching={captchaQuery.isFetching}
            onRefresh={() => captchaQuery.refetch()}
            value={captchaAnswer}
            onChange={setCaptchaAnswer}
            error={fieldErrors.captchaAnswer}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={update.isPending || !captchaAnswer.trim() || !captchaQuery.data}
        >
          {update.isPending ? '…' : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
